/**
 * Operator command palette (⌘K / Ctrl+K) — a Spotlight/Linear-style launcher for
 * the backoffice navigation. Mirrors the platform palette
 * (apps/platform/src/shell/command-palette.tsx): open state + query live in the
 * shared `@hostesa/state` commandPalette slice, the list is gated by the active
 * operator role's RBAC permissions + feature flags (same gate as the sidebar),
 * and cmdk handles fuzzy matching + ↑/↓/↵ keyboard nav. The Dialog provides the
 * scale/fade animation and Esc-to-close.
 *
 * Operator routes are NOT slug-scoped — each nav item navigates to `/${segment}`.
 */
import { useAuth } from '@hostesa/auth-client';
import { useFlags } from '@hostesa/feature-flags';
import { useT } from '@hostesa/i18n';
import {
  closeCommandPalette,
  selectCommandPaletteOpen,
  selectCommandPaletteQuery,
  setCommandPaletteQuery,
  toggleCommandPalette,
  useAppDispatch,
  useAppSelector,
} from '@hostesa/state';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@hostesa/ui';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { NAV_GROUPS } from './nav-config';

/**
 * The operator command palette overlay. Mount once inside the operator shell.
 * Opened by the sidebar `operator-search` button (which dispatches
 * `openCommandPalette`) or the ⌘K / Ctrl+K shortcut.
 */
export function OperatorCommandPalette() {
  const { t } = useT('backoffice');
  const { can } = useAuth();
  const flags = useFlags();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const open = useAppSelector(selectCommandPaletteOpen);
  const query = useAppSelector(selectCommandPaletteQuery);

  // ⌘K / Ctrl+K toggles the palette from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        dispatch(toggleCommandPalette());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dispatch]);

  const go = (segment: string) => {
    dispatch(closeCommandPalette());
    // `to` cast: see lib/nav.ts (platform owns the global router Register).
    void navigate({ to: `/${segment}` as never });
  };

  // Same visibility gate as the sidebar: RBAC permission + optional feature flag.
  const items = NAV_GROUPS.flatMap((group) =>
    group.items
      .filter((item) => can(item.permission))
      .filter((item) => (item.flag ? flags[item.flag] : true)),
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) dispatch(closeCommandPalette());
      }}
    >
      <DialogContent
        hideClose
        data-testid="operator-command-palette"
        className="top-[12vh] max-w-xl translate-y-0 overflow-hidden p-0 shadow-lg"
      >
        <DialogTitle className="sr-only">{t('commandPalette.title')}</DialogTitle>
        <Command>
          <CommandInput
            data-testid="operator-command-palette-input"
            value={query}
            onValueChange={(v) => dispatch(setCommandPaletteQuery(v))}
            placeholder={t('commandPalette.placeholder')}
          />
          <CommandList>
            <CommandEmpty>{t('commandPalette.empty')}</CommandEmpty>
            <CommandGroup heading={t('commandPalette.navHeading')}>
              {items.map((item) => (
                <CommandItem
                  key={item.id}
                  value={t(item.labelKey)}
                  data-testid={`operator-command-item-${item.id}`}
                  onSelect={() => go(item.segment)}
                >
                  {t(item.labelKey)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
